import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import ImageField from "./ImageField";

const inputCls =
  "w-full bg-white border border-[#E5DED4] focus:border-[#9A7B66] rounded-md px-3 py-2 text-sm outline-none transition-colors";

const Label = ({ children }) => (
  <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A7B66] mb-1.5">{children}</label>
);

export default function SeoTab() {
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => toast.error("Failed to load settings"));
  }, []);

  if (!form) return <p className="text-sm text-[#A0988E]">Loading...</p>;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    try {
      await api.put("/admin/settings", form);
      toast.success("SEO & links saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="seo-tab" className="max-w-2xl">
      <h2 className="font-serif text-2xl text-[#2C2825] mb-1">SEO & Links</h2>
      <p className="text-sm text-[#A0988E] mb-6">
        Search engine meta, navigation menu, section visibility, and every link on the website.
      </p>

      <div className="bg-white border border-[#E5DED4] rounded-md p-6 space-y-5">
        <h3 className="font-serif text-lg text-[#2C2825]">Search Engine (SEO)</h3>
        <div>
          <Label>Meta Title (appears as the Google result title)</Label>
          <input
            data-testid="seo-meta-title-input"
            value={form.meta_title || ""}
            onChange={(e) => set("meta_title", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <Label>Meta Description (appears in Google search results)</Label>
          <textarea
            data-testid="seo-meta-description-input"
            rows={2}
            value={form.meta_description || ""}
            onChange={(e) => set("meta_description", e.target.value)}
            className={`${inputCls} resize-y`}
          />
        </div>
        <div>
          <Label>Meta Keywords (comma separated)</Label>
          <input
            data-testid="seo-meta-keywords-input"
            value={form.meta_keywords || ""}
            onChange={(e) => set("meta_keywords", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <Label>WhatsApp / Social Share Image (leave empty to use the first live Hero photo automatically)</Label>
          <ImageField testId="seo-og-image-input" value={form.og_image} onChange={(v) => set("og_image", v)} accept="image/*" />
        </div>
        <div>
          <Label>Social Share Image Alt Text</Label>
          <input
            data-testid="seo-og-image-alt-input"
            value={form.og_image_alt || ""}
            onChange={(e) => set("og_image_alt", e.target.value)}
            className={inputCls}
          />
        </div>

        <h3 className="font-serif text-lg text-[#2C2825] pt-4 border-t border-[#E5DED4]">Navigation & Pages</h3>
        <div>
          <Label>Navigation Menu - one per line: Label | section-id</Label>
          <textarea
            data-testid="seo-nav-menu-input"
            rows={8}
            value={form.nav_menu || ""}
            onChange={(e) => set("nav_menu", e.target.value)}
            placeholder={"About | about\nStay | villas\nDining | dining\nExperiences | experiences\nOffers | offers\nGallery | gallery\nBlog | blog\nContact Us | contact"}
            className={`${inputCls} resize-y font-mono text-xs`}
          />
          <p className="text-xs text-[#A0988E] mt-1.5">
            Controls navbar + footer menu labels and order. Note: the top navbar always shows only the "about" and
            "offers" entries - all entries appear in the full-screen menu. Section ids: about, villas, dining,
            experiences, offers, gallery, blog, contact. Leave empty for defaults.
          </p>
        </div>
        <div>
          <Label>Section Visibility - matikan untuk menyembunyikan section di website</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="section-visibility-toggles">
            {[
              ["about", "About"],
              ["villas", "Stay / Villas"],
              ["dining", "Dining"],
              ["experiences", "Experiences"],
              ["offers", "Offers & Promos"],
              ["schedule", "Boat Schedule"],
              ["gallery", "Gallery"],
              ["reviews", "Guest Reviews"],
              ["blog", "Blog"],
              ["contact", "Contact"],
            ].map(([id, label]) => {
              const hiddenList = String(form.hidden_sections || "").split(",").map((s) => s.trim()).filter(Boolean);
              const visible = !hiddenList.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`section-visibility-toggle-${id}`}
                  aria-pressed={visible}
                  onClick={() => {
                    const next = visible ? [...hiddenList, id] : hiddenList.filter((x) => x !== id);
                    set("hidden_sections", next.join(", "));
                  }}
                  className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider transition-colors ${
                    visible
                      ? "border-[#9A7B66]/40 bg-[#F4EFEA] text-[#2C2825]"
                      : "border-[#E5DED4] bg-white text-[#A0988E]"
                  }`}
                >
                  {label}
                  <span className={`relative w-8 rounded-full shrink-0 transition-colors ${visible ? "bg-[#9A7B66]" : "bg-[#D8CBB8]"}`} style={{ height: 18 }}>
                    <span className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${visible ? "translate-x-[14px]" : "translate-x-[2px]"}`} />
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-[#A0988E] mt-2">
            Section yang dimatikan langsung hilang dari website dan tata letak menyesuaikan tanpa ruang kosong. Klik Save untuk menyimpan.
          </p>
        </div>

        <h3 className="font-serif text-lg text-[#2C2825] pt-4 border-t border-[#E5DED4]">Links</h3>
        <div>
          <Label>Social Links - one per line: Label | https://url</Label>
          <textarea
            data-testid="seo-social-links-input"
            rows={2}
            value={form.social_links || ""}
            onChange={(e) => set("social_links", e.target.value)}
            placeholder={"Instagram | https://instagram.com/nusadira"}
            className={`${inputCls} resize-y font-mono text-xs`}
          />
        </div>
        <div>
          <Label>Footer Backlinks (SEO) - one per line: Label | https://url</Label>
          <textarea
            data-testid="seo-footer-links-input"
            rows={3}
            value={form.footer_links || ""}
            onChange={(e) => set("footer_links", e.target.value)}
            placeholder={"Armella Bali | https://armellabali.com"}
            className={`${inputCls} resize-y font-mono text-xs`}
          />
        </div>

        <button
          data-testid="seo-save-button"
          onClick={save}
          className="px-8 py-3 rounded-sm bg-[#9A7B66] hover:bg-[#826552] text-white text-xs font-semibold uppercase tracking-wider transition-colors"
        >
          Save SEO & Links
        </button>
      </div>
    </div>
  );
}
